<script setup lang="ts">
import Signpost from '#assets/svg/signpost.svg';
import { useLanguage } from '#composables/useLanguage';
import { isPeriodGranularity } from '#domain/core/filter/period';
import { PERIOD_FIELD } from '#stores/utils/filterMapping/constants/filterMapping.constant';
import { C4InputCheckboxGroup, C4InputRadioGroup, C4InputSelect, C4PopOver } from '@c4-links/vue3-links-components';
import { FilterDefinitionFormType } from '@carrefour-gcs/bff-sdk';
import { CIcon, CInputText } from '@carrefour/design-system-components-vue3';
import { useField } from 'vee-validate';
import { computed, ref } from 'vue';
import AccordionField from './fields/accordionField/AccordionField.vue';
import PeriodField from './fields/PeriodField/PeriodField.vue';
import type { DynamicFormFieldPropsType } from './types/dynamicFormFieldProps.type';
import { resolveFilterValueRestriction } from './utils/filterRestriction.utils';
import { RESTRICTED_VALUE_MESSAGE } from './constants/dynamicFormSchema.constant.ts';

const { t } = useLanguage();

// Template refs for checkbox tree fields to reset selections on cancel
const accordionFieldRef = ref<InstanceType<typeof AccordionField>>();

const { field, maxDate, granularity, formValues } = defineProps<DynamicFormFieldPropsType>();

const periodGranularity = computed(() => (isPeriodGranularity(granularity) ? granularity : undefined));
const isDateField = computed(() =>
  [FilterDefinitionFormType.DATE, FilterDefinitionFormType.DATE_RANGE].includes(field.element)
);

const getInitialValue = () => {
  if (field.element === FilterDefinitionFormType.CHECKBOX_TREE) {
    return field.initialValue ?? [];
  }

  if (
    [
      FilterDefinitionFormType.SELECT,
      FilterDefinitionFormType.CHECKBOX,
      FilterDefinitionFormType.CHECKBOX_GRID,
      FilterDefinitionFormType.CHECKBOX_GROUP
    ].includes(field.element) &&
    !field.initialValue
  ) {
    return [];
  }

  return field.initialValue as unknown;
};

const { value: fieldValue, errorMessage } = useField<unknown>(() => field.fieldName, undefined, {
  initialValue: getInitialValue()
});

// Extracted to a computed: a union type cast inline in the template mustache is misparsed
// by vue-eslint-parser as legacy filter syntax (vue/no-deprecated-filter).
const periodFieldValue = computed(() => fieldValue.value as string | undefined);

const shouldShowIcon = computed(() => !!(field.icon && field.icon !== 'signpost'));

const getTranslatedOptions = computed(() => {
  if (!field.options) {
    return [];
  }

  return field.options.map((e) => {
    const { disabled, disabledReasonTranslationKey } = resolveFilterValueRestriction(e.restrictions, formValues);

    return {
      label: t(e.label),
      value: e.value as string,
      disabled,
      disabledReasonTranslationKey
    };
  });
});

/**
 * Reasons of the options disabled by their restrictions, displayed in the info popover of the field.
 */
const disabledReasons = computed(() =>
  getTranslatedOptions.value
    .filter((option) => option.disabled && option.disabledReasonTranslationKey)
    .map((option) => t(`${RESTRICTED_VALUE_MESSAGE}.${option.disabledReasonTranslationKey}`))
);

defineExpose({
  resetCheckboxTreeSelections: () => accordionFieldRef.value?.resetCheckboxTreeSelections()
});
</script>

<template>
  <div class="flex flex-col">
    <div>
      <template
        v-if="
          [
            FilterDefinitionFormType.CHECKBOX_TREE,
            FilterDefinitionFormType.CHECKBOX_CARD,
            FilterDefinitionFormType.CHECKBOX_GROUP,
            FilterDefinitionFormType.CHECKBOX_GRID
          ].includes(field.element)
        "
      >
        <AccordionField
          ref="accordionFieldRef"
          :field="field"
          :should-show-icon="shouldShowIcon"
          @update:field-value="(event) => (fieldValue = event)"
        />
      </template>

      <template v-else>
        <div class="relative mx-4 py-1 flex align-middle justify-start">
          <div class="w-48 flex items-center">
            <Signpost v-if="field.icon === 'signpost'" class="[&_path]:fill-black mr-1"></Signpost>

            <CIcon v-if="shouldShowIcon" class="mr-2 text-2xl" :name="field.icon"></CIcon>
            <label class="flex items-center text-sm" :for="field.fieldName">
              {{ t(field.fieldLabel) }}
            </label>
          </div>

          <div class="basis-8/12 flex align-middle justify-start">
            <PeriodField
              v-if="isDateField"
              :name="field.fieldName"
              :model-value="periodFieldValue"
              :granularity="periodGranularity"
              :with-presets="field.fieldName === PERIOD_FIELD"
              :max-date="maxDate"
              @update:model-value="fieldValue = $event"
            />

            <CInputText
              v-else-if="field.element === FilterDefinitionFormType.TEXT"
              :id="field.fieldName"
              v-model="fieldValue as string"
              class="w-80 shrink-0 grow-0"
              :placeholder="t(field.fieldLabel)"
              size="s"
            >
            </CInputText>

            <div v-else-if="field.element === FilterDefinitionFormType.SELECT" class="shrink-0">
              <C4InputSelect
                v-model="fieldValue as string"
                :label="field.fieldLabel"
                :name="field.fieldName"
                :options="getTranslatedOptions"
                class="w-80 font-bold"
                size="small"
                type="single"
                variant="default"
              ></C4InputSelect>
            </div>

            <div v-else-if="field.element === FilterDefinitionFormType.RADIO">
              <C4InputRadioGroup
                v-model="fieldValue as string"
                class="flex [&_label]:p-2 gap-1 align-middle items-center flex-wrap shrink-1 [&_div]:grow-0 font-bold"
                :style="'horizontal'"
                :options="getTranslatedOptions"
              />
            </div>

            <div v-else-if="field.element === FilterDefinitionFormType.CHECKBOX">
              <C4InputCheckboxGroup
                v-model="fieldValue as string[]"
                class="[&_label]:p-2 flex flex-wrap gap-1 align-middle items-center shrink-1 [&_div]:grow-0 font-bold"
                :options="getTranslatedOptions"
                :inline="true"
              ></C4InputCheckboxGroup>
            </div>

            <C4PopOver v-if="disabledReasons.length > 0" class="self-center ml-1">
              <VueFeather type="info" size="14" class="text-default-tertiary" />

              <template #pop-over>
                <p v-for="reason in disabledReasons" :key="reason">
                  {{ reason }}
                </p>
              </template>
            </C4PopOver>
          </div>
        </div>
      </template>
      <span v-if="errorMessage" class="text-error">{{ t(errorMessage) }}</span>
    </div>
  </div>
</template>

<style lang="css" scoped src="./dynamicFormField.css"></style>
